import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import "realm-web/types/realm/services.d.ts";
import { ConditionalContextRender } from "./ConditionalContextRender";
import * as Realm from "./Realm";
import { UserCustomData } from "./Realm.interfaces";
import { RealmContext } from "./RealmContext";
import * as BSON from 'bson';

type RealmProviderProps = {
  id: string;
  render?: Function;
  remember?: boolean;
  anon?: boolean;
};

const stringToObjectIdCache: { [key: string]: BSON.ObjectId; } = {};

const stringToObjectId = (id: string): BSON.ObjectId =>
  (stringToObjectIdCache[id] ||= new BSON.ObjectId(id));

export const RealmProvider = ({ id = import.meta.env.VITE_STITCH_ENV, render, remember = true, anon = false, children }: PropsWithChildren<RealmProviderProps>) => {
  const app = useMemo(() => new Realm.App({ id }), [id]);

  const [realmUser, setRealmUser] = useState<Realm.User | undefined>((remember && app.currentUser) || undefined);
  const [realmUserData, setRealmUserData] = useState(
    (realmUser?.customData || {}) as UserCustomData
  );
  // const isInternal = useMemo(() => Boolean(realmUserData?.email?.match(/\@cleandns\.(?:email|com)$/)), [realmUserData?.email]);
  const [loading, setLoading] = useState(Boolean(realmUser));
  const isLoggedIn = Boolean(realmUser && realmUser.isLoggedIn);
  const mongo = useMemo(
    () => realmUser?.mongoClient("mongodb-atlas")!,
    [realmUser]
  );
  const db = useMemo(() => mongo?.db("v5")!, [mongo]);
  const dbClientData = useMemo(() => mongo?.db("client-data")!, [mongo]);

  const apiToken = realmUserData?.apiToken?.encryptedKey || "";

  const updateUser = useCallback(
    async (user: Realm.User, update?: globalThis.Realm.Services.MongoDB.Filter) => {
      const okay = user && !update;
      let data: any;

      if (okay) {
        data = BSON.EJSON.parse(BSON.EJSON.stringify(user.customData));
        data.id = user.customData._id;
        data._id = stringToObjectId(data.id);
        setRealmUser(user);
        setRealmUserData(data);
      } else {
        setRealmUser(undefined);
        setRealmUserData({} as UserCustomData);
      }

      setLoading(false);
      return data;
    },
    []
  );

  const callFunction = useCallback(
    (name: string, ...args: any[]) => realmUser!.callFunction(name, ...args),
    [realmUser]
  );

  const callFunctionRaw = useCallback(
    (name: string, ...args: any[]) => realmUser!.callFunctionRaw(name, ...args),
    [realmUser]
  );

  const login = useCallback(
    async (how: string, creds?: Realm.Credentials, onLogin?: Function) => {
      setLoading(true);
      // try to login
      const user: Realm.User = await app
        .logIn(
          // @ts-ignore
          Realm.Credentials[how](...(Array.isArray(creds) ? creds : [creds]))
        )
        .catch((error) => {
          setLoading(false);
          throw error;
        });

      // make sure customData is up-to-date
      await user.refreshCustomData().catch((error) => {
        setLoading(false);
        throw error;
      })

      if (onLogin) await onLogin(user);
      await updateUser(user).catch((error) => {
        setLoading(false);
        throw error;
      });
      return user;
    },
    [app, updateUser]
  );

  const refreshUser = useCallback(
    async (update?: globalThis.Realm.Services.MongoDB.Filter) => updateUser(realmUser!, update),
    [realmUser, updateUser]
  );

  const logout = useCallback(async () => {
    realmUser && realmUser.logOut().then(() => setRealmUser(undefined));
  }, [realmUser]);

  const context = {
    app,
    realmUser,
    realmUserData,
    isLoggedIn,
    db,
    dbClientData,
    mongo,
    login,
    logout,
    refreshUser,
    loading,
    callFunction,
    callFunctionRaw,
    apiToken,
  };

  return (
    <RealmContext.Provider value={context}>
      {render ? render(context) : children}
    </RealmContext.Provider>
  );
};

type RealmProviderExtra = Partial<{
  isLoggedIn: boolean;
  loading: boolean;
}>;

RealmProvider.LoggedIn = ConditionalContextRender(
  RealmContext,
  ({ isLoggedIn }: RealmProviderExtra) => isLoggedIn
);
RealmProvider.LoggedOut = ConditionalContextRender(
  RealmContext,
  ({ isLoggedIn }: RealmProviderExtra) => !isLoggedIn
);
RealmProvider.Loading = ConditionalContextRender(
  RealmContext,
  ({ loading }: RealmProviderExtra) => loading
);
RealmProvider.Ready = ConditionalContextRender(
  RealmContext,
  ({ loading }: RealmProviderExtra) => !loading
);

export default RealmProvider;

